import { useState, useCallback, useEffect } from "@wordpress/element";

// LocalStorage keys
const SCANNED_KEY = "scannedUrls";

function readScanned() {
	try {
		return JSON.parse(localStorage.getItem(SCANNED_KEY)) || [];
	} catch (e) {
		console.error("Failed to parse scanned urls", e);
		return [];
	}
}

export default function useScanStorage() {
	const [scanned, setScanned] = useState(() => readScanned());

	const refresh = useCallback(() => {
		setScanned(readScanned());
	}, []);

	const saveList = useCallback((list) => {
		try {
			localStorage.setItem(SCANNED_KEY, JSON.stringify(list));
			document.cookie = `scannedUrls=${encodeURIComponent(
				JSON.stringify(list),
			)}; path=/`;
		} catch (e) {
			console.error("Failed saving scanned list", e);
		}
		setScanned(list);
	}, []);

	const upsertRecord = useCallback(
		(record) => {
			const list = readScanned().filter((r) => r.url !== record.url);
			list.push(record);
			saveList(list);
		},
		[saveList],
	);

	// Update resolved counts for a URL. Accepts either a number (legacy) or an object
	// { total, A, AA, AAA } produced by use-scan-results.
	const updateResolvedForUrl = useCallback(
		(url, resolvedInfo) => {
			const list = readScanned().map((r) => {
				if (r.url !== url) return r;
				if (typeof resolvedInfo === "number") {
					return {
						...r,
						issuesResolved: Math.min(resolvedInfo, r.totalIssues),
						resolvedLevelA: r.resolvedLevelA || 0,
						resolvedLevelAA: r.resolvedLevelAA || 0,
						resolvedLevelAAA: r.resolvedLevelAAA || 0,
					};
				}
				// object path
				return {
					...r,
					issuesResolved: Math.min(resolvedInfo.total, r.totalIssues),
					resolvedLevelA: resolvedInfo.A ?? r.resolvedLevelA ?? 0,
					resolvedLevelAA: resolvedInfo.AA ?? r.resolvedLevelAA ?? 0,
					resolvedLevelAAA: resolvedInfo.AAA ?? r.resolvedLevelAAA ?? 0,
				};
			});
			saveList(list);
		},
		[saveList],
	);

	const stats = scanned.reduce(
		(acc, page) => {
			acc.totalIssues += page.totalIssues || 0;
			acc.totalResolved += page.issuesResolved || 0;
			acc.levelCounts.A += page.levelA || 0;
			acc.levelCounts.AA += page.levelAA || 0;
			acc.levelCounts.AAA += page.levelAAA || 0;
			acc.levelResolvedCounts.A += page.resolvedLevelA || 0;
			acc.levelResolvedCounts.AA += page.resolvedLevelAA || 0;
			acc.levelResolvedCounts.AAA += page.resolvedLevelAAA || 0;
			return acc;
		},
		{
			totalIssues: 0,
			totalResolved: 0,
			levelCounts: { A: 0, AA: 0, AAA: 0 },
			levelResolvedCounts: { A: 0, AA: 0, AAA: 0 },
		},
	);

	// Keep in sync if other tabs modify
	useEffect(() => {
		const onStorage = (e) => {
			if (e.key === SCANNED_KEY) refresh();
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, [refresh]);

	return {
		scanned,
		refresh,
		upsertRecord,
		updateResolvedForUrl,
		stats,
	};
}
